import { currentRoute, describe } from "./identify";

// Automatic error capture. Errors are grouped in the browser by a coarse key and sent
// in batches with a count, so a render loop throwing 1000 times costs one request and
// one database write, not 1000.

export type Crumb = { t: number; type: "click" | "nav" | "net"; text: string };

export type ErrorCaptureOptions = {
  endpoint: string;
  key: string;
  attributes: string[];
  console: boolean;
  network: boolean;
  context: () => Record<string, unknown>;
  reporter: () => string;
};

type Pending = { message: string; stack?: string; source: string; url: string; route: string; count: number; target?: unknown };

const MAX_DISTINCT = 100;
const FLUSH_MS = 5000;

export const breadcrumbs: Crumb[] = [];

export function crumb(type: Crumb["type"], text: string) {
  breadcrumbs.push({ t: Date.now(), type, text: text.slice(0, 160) });
  if (breadcrumbs.length > 20) breadcrumbs.shift();
}

export function installErrorCapture(opts: ErrorCaptureOptions, isOwn: (el: EventTarget | null) => boolean) {
  const pending = new Map<string, Pending>();
  const seen = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | undefined;
  /** null until the server says whether this key captures errors; errors wait meanwhile. */
  let enabled: boolean | null = null;

  function capture(source: string, message: string, stack?: string, target?: unknown) {
    try {
      const firstFrame = stack?.split("\n").find((l) => /:\d+:\d+/.test(l)) ?? "";
      const k = `${source}|${message}|${firstFrame}`;
      const existing = pending.get(k);
      if (enabled === false) return;
      if (existing) return void existing.count++;
      if (!seen.has(k) && seen.size >= MAX_DISTINCT) return;
      seen.add(k);
      pending.set(k, { message, stack, source, url: location.href, route: currentRoute(), count: 1, target });
      timer ??= setTimeout(flush, FLUSH_MS);
    } catch {
      // Never let the reporter itself break the host page.
    }
  }

  function flush() {
    clearTimeout(timer);
    timer = undefined;
    if (!pending.size || enabled === false) return;
    // Still waiting to hear whether capture is on: hold on to them.
    if (enabled === null) return void (timer = setTimeout(flush, FLUSH_MS));
    const context = { ...opts.context(), breadcrumbs: breadcrumbs.slice() };
    const events = [...pending.values()].map((e) => ({ ...e, context, reporter: opts.reporter() }));
    pending.clear();
    const body = JSON.stringify({ key: opts.key, events });
    const url = `${opts.endpoint}/v1/events`;
    // text/plain keeps this a CORS "simple request" (no preflight); keepalive survives unload.
    if (document.visibilityState === "hidden" && navigator.sendBeacon) navigator.sendBeacon(url, body);
    else fetch(url, { method: "POST", body, keepalive: true, mode: "cors" }).catch(() => {});
  }

  addEventListener(
    "error",
    (e) => {
      const el = e.target as Element | null;
      if (el && el !== (window as unknown as Element) && el instanceof Element) {
        // Resource failed to load (img, script, link). These do not bubble, hence capture phase.
        if (isOwn(el)) return;
        const src = (el as HTMLImageElement).src || (el as HTMLLinkElement).href || "";
        capture("resource", `Failed to load <${el.tagName.toLowerCase()}> ${src.split("?")[0]}`, undefined, describe(el, opts.attributes));
        return;
      }
      const err = (e as ErrorEvent).error;
      capture("onerror", (e as ErrorEvent).message || String(err), err?.stack);
    },
    true,
  );

  addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    capture("unhandledrejection", r instanceof Error ? `${r.name}: ${r.message}` : safeString(r), r?.stack);
  });

  if (opts.console) {
    const original = console.error;
    console.error = function (...args: unknown[]) {
      const err = args.find((a): a is Error => a instanceof Error);
      capture("console", args.map(safeString).join(" ").slice(0, 1000), err?.stack);
      return original.apply(this, args);
    };
  }

  if (opts.network) patchNetwork(opts.endpoint, capture);

  addEventListener("visibilitychange", () => document.visibilityState === "hidden" && flush());
  addEventListener("pagehide", flush);

  /** Apply the key's capture mode from the server: off drops everything held. */
  function setEnabled(on: boolean) {
    enabled = on;
    if (!on) pending.clear();
    else if (pending.size) flush();
  }

  return { capture, flush, setEnabled };
}

function patchNetwork(ownEndpoint: string, capture: (source: string, message: string) => void) {
  const report = (method: string, url: string, status: number | string) => {
    if (url.startsWith(ownEndpoint)) return;
    const path = url.replace(location.origin, "").split("?")[0];
    crumb("net", `${method} ${path} → ${status}`);
    capture("network", `${method.toUpperCase()} ${path} → ${status}`);
  };

  const originalFetch = window.fetch;
  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
    const method = init?.method ?? (input instanceof Request ? input.method : "GET");
    const url = input instanceof Request ? input.url : String(input);
    try {
      const res = await originalFetch.call(this, input, init);
      if (res.status >= 500) report(method, url, res.status);
      return res;
    } catch (err) {
      if ((err as Error)?.name !== "AbortError") report(method, url, "network error");
      throw err;
    }
  };

  const open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    let aborted = false;
    this.addEventListener("abort", () => (aborted = true));
    this.addEventListener("loadend", () => {
      if (this.status >= 500 || (this.status === 0 && !aborted)) report(method, String(url), this.status || "network error");
    });
    return (open as any).call(this, method, url, ...rest);
  } as typeof open;
}

function safeString(v: unknown): string {
  if (typeof v === "string") return v;
  if (v instanceof Error) return `${v.name}: ${v.message}`;
  try {
    return JSON.stringify(v)?.slice(0, 500) ?? String(v);
  } catch {
    return String(v);
  }
}
